/**
 * What selection looks like, and how a layer is resized.
 *
 * Konva's Transformer is the obvious answer and the wrong one here: it attaches
 * to nodes and scales them, which would write a scale factor onto a layer whose
 * size is measured in millimetres. Selection draws its own outline, and the
 * handles write millimetres straight back to the document.
 *
 * The accent is spent here, and nowhere else on the canvas. §6.3.
 */

import { useCallback, useRef } from 'react';
import { Circle, Group, Path, Rect } from 'react-konva';
import type Konva from 'konva';
import { useEditor, useSelectedLayers } from '../state/store';
import { mmToPx, pxToMm, roundMm } from '../model/units';
import type { Layer } from '../model/types';
import { frameOf } from './frame';
import { snap } from './snapping';

const ACCENT = '#dd1d26';

/*
 * Canva's handles: round at the corners, pills along the sides, white with a
 * soft edge so they read on any artwork. A corner is a circle of this size; a
 * side is a pill this long by HANDLE wide.
 */
const HANDLE = 10;
const PILL = 18;
const RIM = '#c9c5bf';

/** Nothing may be resized smaller than this. Below it there is nothing to grab. */
const MIN_MM = 1;

/** Which edges a handle moves. */
type Grip = { corner: [number, number]; edges: { left?: true; right?: true; top?: true; bottom?: true } };

const GRIPS: Grip[] = [
  { corner: [0, 0], edges: { left: true, top: true } },
  { corner: [0.5, 0], edges: { top: true } },
  { corner: [1, 0], edges: { right: true, top: true } },
  { corner: [1, 0.5], edges: { right: true } },
  { corner: [1, 1], edges: { right: true, bottom: true } },
  { corner: [0.5, 1], edges: { bottom: true } },
  { corner: [0, 1], edges: { left: true, bottom: true } },
  { corner: [0, 0.5], edges: { left: true } },
];

const CURSORS = ['nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize'];

export function SelectionBox({ zoom }: { zoom: number }) {
  const selected = useSelectedLayers();
  const preview = useEditor((s) => s.view.preview);
  const cropping = useEditor((s) => s.cropping);
  const shaping = useEditor((s) => s.shaping);

  /*
   * Cropping draws its own frame and owns the pointer inside it. Leaving the
   * resize handles on top would put two red rectangles in the same place and
   * offer, in the middle of choosing what a photograph shows, a grip that
   * changes the size of the frame instead.
   *
   * Editing a drawing's points is the same: its anchors sit on the outline,
   * which is exactly where the resize handles would be.
   */
  if (preview || cropping || shaping || selected.length === 0) return null;

  const single = selected.length === 1 ? selected[0] : null;

  return (
    <>
      {selected.map((layer) => (
        <Outline key={layer.id} layer={layer} zoom={zoom} />
      ))}

      {selected.length > 1 && <Bounds layers={selected} zoom={zoom} />}

      {/* Handles only for a single selection. Eight handles on each of five
          objects is noise, not affordance. */}
      {single && !single.locked && <Handles layer={single} zoom={zoom} />}
      {single && !single.locked && <Rotator layer={single} zoom={zoom} />}
    </>
  );
}

function Outline({ layer, zoom }: { layer: Layer; zoom: number }) {
  const w = mmToPx(layer.w, zoom);
  const h = mmToPx(layer.h, zoom);

  return (
    <Group {...frameOf(layer, zoom)} listening={false}>
      <Rect
        width={w}
        height={h}
        stroke={ACCENT}
        strokeWidth={2}
        strokeScaleEnabled={false}
        dash={layer.locked ? [4, 3] : undefined}
      />
    </Group>
  );
}

function Handles({ layer, zoom }: { layer: Layer; zoom: number }) {
  const w = mmToPx(layer.w, zoom);
  const h = mmToPx(layer.h, zoom);

  // Named, for the same reason the crop frame is: a handle belongs to its
  // layer, and pressing one without moving it must not read as a click on
  // paper and drop the selection it is attached to.
  return (
    /*
     * Turned with the layer, but never mirrored with it.
     *
     * Flipping mirrors what a layer shows, not the box it occupies: a flipped
     * picture still has the same left edge in millimetres. Drawing the handles
     * in a mirrored frame put the left grip on the right of the object and,
     * worse, measured every drag backwards - the pointer went right and the
     * object grew to the left. The rotator has always been upright for the
     * same reason.
     */
    <Group name={layer.id} {...frameOf(layer, zoom)} scaleX={1} scaleY={1}>
      {GRIPS.map((grip, index) => (
        <Handle
          key={index}
          grip={grip}
          cursor={CURSORS[index] ?? 'pointer'}
          layer={layer}
          zoom={zoom}
          w={w}
          h={h}
        />
      ))}
    </Group>
  );
}

function Handle({
  grip,
  cursor,
  layer,
  zoom,
  w,
  h,
}: {
  grip: Grip;
  cursor: string;
  layer: Layer;
  zoom: number;
  w: number;
  h: number;
}) {
  const handle = useRef<Konva.Rect>(null);

  /**
   * Resizes in the layer's own frame, not the page's.
   *
   * getRelativePointerPosition() on the parent group gives the pointer in
   * exactly that frame, rotation already undone - which is the whole reason
   * the handles live inside a rotated group rather than being positioned by
   * trigonometry out here.
   */
  const onDragMove = useCallback(() => {
    const node = handle.current;
    const parent = node?.getParent();

    if (!node || !parent) return;

    const local = parent.getRelativePointerPosition();

    if (!local) return;

    const store = useEditor.getState();
    const page = store.doc?.pages[store.pageIndex];

    if (!page || !store.doc) return;

    const at = { x: pxToMm(local.x, zoom), y: pxToMm(local.y, zoom) };

    let { x, y, w: width, h: height } = layer;

    if (grip.edges.left) {
      const right = x + width;
      x = Math.min(x + at.x, right - MIN_MM);
      width = right - x;
    }

    if (grip.edges.right) width = Math.max(MIN_MM, at.x);

    if (grip.edges.top) {
      const bottom = y + height;
      y = Math.min(y + at.y, bottom - MIN_MM);
      height = bottom - y;
    }

    if (grip.edges.bottom) height = Math.max(MIN_MM, at.y);

    // Only an unrotated box can snap meaningfully to page geometry - once it
    // is turned, its edges are not the edges the guides are about.
    if (layer.rotation === 0) {
      const ignore = new Set([layer.id]);
      const moved = snap({ x, y, w: width, h: height }, page, store.doc.safe, zoom, ignore);

      if (grip.edges.left) {
        width += x - moved.x;
        x = moved.x;
      }

      if (grip.edges.top) {
        height += y - moved.y;
        y = moved.y;
      }

      store.setGuides(moved.guides);
    }

    store.updateLayer(
      layer.id,
      'Resize',
      (l) => ({
        ...l,
        x: roundMm(x),
        y: roundMm(y),
        w: roundMm(Math.max(MIN_MM, width)),
        h: roundMm(Math.max(MIN_MM, height)),
      }),
      // The whole gesture is one undo step.
      { coalesce: `resize:${layer.id}` }
    );

    // React puts the handle back where the layer's new size says it goes; if
    // Konva kept the dragged position too, the two would fight.
    node.position(place);
  }, [grip, layer, zoom, w, h]);

  // A corner is a circle; a side is a pill lying along its edge.
  const side = grip.corner[0] === 0.5 || grip.corner[1] === 0.5;
  const across = grip.corner[1] === 0.5;
  const hw = side ? (across ? HANDLE * 0.6 : PILL) : HANDLE;
  const hh = side ? (across ? PILL : HANDLE * 0.6) : HANDLE;
  const place = { x: grip.corner[0] * w - hw / 2, y: grip.corner[1] * h - hh / 2 };

  // A side pill has no room on a tiny object; the corners are enough there.
  if (side && (across ? h < PILL * 2 : w < PILL * 2)) return null;

  return (
    <Rect
      ref={handle}
      x={place.x}
      y={place.y}
      width={hw}
      height={hh}
      fill="#ffffff"
      stroke={RIM}
      strokeWidth={1}
      strokeScaleEnabled={false}
      cornerRadius={Math.min(hw, hh) / 2}
      shadowColor="rgba(0,0,0,0.25)"
      shadowBlur={3}
      shadowOffsetY={1}
      // A little extra to grab on the small corner circles; a side pill takes
      // only what it shows, or a click at the end of a line of text - to put
      // the caret there - catches the pill instead.
      hitStrokeWidth={side ? 0 : 8}
      draggable
      onDragMove={onDragMove}
      onDragEnd={() => useEditor.getState().setGuides([])}
      onMouseEnter={(event) => {
        const stage = event.target.getStage();

        if (stage) stage.container().style.cursor = cursor;
      }}
      onMouseLeave={(event) => {
        const stage = event.target.getStage();

        if (stage) stage.container().style.cursor = '';
      }}
    />
  );
}

/**
 * The rotation handle, below the object, as Canva places it.
 *
 * Away from the resize handles on purpose: the two gestures are a few pixels
 * apart in most editors and getting the wrong one is a constant small
 * annoyance. Below rather than above, because the floating toolbar and the
 * object's own menu live above. Shift snaps to 15 degrees, which is what every
 * design tool does and what makes a deliberate 45 possible with a mouse.
 *
 * In the layer's own frame (frameOf), so it orbits the object's middle - it
 * used to turn about the top-left corner, and on a turned object sat somewhere
 * other than where the object was.
 */
function Rotator({ layer, zoom }: { layer: Layer; zoom: number }) {
  const knob = useRef<Konva.Group>(null);

  const w = mmToPx(layer.w, zoom);
  const h = mmToPx(layer.h, zoom);
  const stalk = 26;
  const frame = frameOf(layer, zoom);
  // The knob never mirrors with the object: a flipped object still has its
  // handle below it on screen.
  const upright = { ...frame, scaleX: 1, scaleY: 1 };

  const onDragMove = useCallback(
    (event: Konva.KonvaEventObject<DragEvent>) => {
      const node = knob.current;
      const stage = node?.getStage();

      if (!node || !stage) return;

      const pointer = stage.getPointerPosition();

      if (!pointer) return;

      // The angle from the object's centre to the pointer, in stage
      // coordinates - which is why the centre is asked of Konva rather than
      // computed from millimetres: the group it sits in is already rotated.
      const parent = node.getParent();
      const centre = parent
        ? parent.getAbsoluteTransform().point({ x: w / 2, y: h / 2 })
        : { x: 0, y: 0 };

      const radians = Math.atan2(pointer.y - centre.y, pointer.x - centre.x);

      // The handle sits below the object, so straight down is zero rotation.
      let degrees = (radians * 180) / Math.PI - 90;

      if (event.evt.shiftKey) degrees = Math.round(degrees / 15) * 15;

      degrees = ((degrees % 360) + 360) % 360;

      useEditor.getState().updateLayer(
        layer.id,
        'Turn',
        (l) => ({ ...l, rotation: Math.round(degrees * 10) / 10 }),
        { coalesce: `rotate:${layer.id}` }
      );

      // React puts the knob back where the new rotation says it goes.
      node.position({ x: w / 2, y: h + stalk });
    },
    [layer.id, w, h]
  );

  return (
    // Named for its layer, like the handles: pressing it is pressing the
    // object, not bare paper, so it starts no selection band.
    <Group name={layer.id} {...upright}>
      <Group
        ref={knob}
        id="tb-rotator"
        x={w / 2}
        y={h + stalk}
        draggable
        onDragMove={onDragMove}
        onMouseEnter={(event) => {
          const stage = event.target.getStage();

          if (stage) stage.container().style.cursor = 'grab';
        }}
        onMouseLeave={(event) => {
          const stage = event.target.getStage();

          if (stage) stage.container().style.cursor = '';
        }}
      >
        <Circle
          radius={12}
          fill="#ffffff"
          stroke={RIM}
          strokeWidth={1}
          strokeScaleEnabled={false}
          shadowColor="rgba(0,0,0,0.25)"
          shadowBlur={4}
          shadowOffsetY={1}
        />
        {/* A circular arrow, as every tool draws "turn". */}
        <Path
          data="M 4.6 -2.6 A 5.2 5.2 0 1 0 5.2 1.4 M 4.9 -5.4 L 4.9 -2.3 L 1.8 -2.3"
          stroke="#3a3d42"
          strokeWidth={1.6}
          lineCap="round"
          lineJoin="round"
          listening={false}
        />
      </Group>
    </Group>
  );
}

/** One faint box around a multiple selection, so it reads as one thing. */
function Bounds({ layers, zoom }: { layers: Layer[]; zoom: number }) {
  const left = Math.min(...layers.map((l) => l.x));
  const top = Math.min(...layers.map((l) => l.y));
  const right = Math.max(...layers.map((l) => l.x + l.w));
  const bottom = Math.max(...layers.map((l) => l.y + l.h));

  return (
    <Rect
      x={mmToPx(left, zoom)}
      y={mmToPx(top, zoom)}
      width={mmToPx(right - left, zoom)}
      height={mmToPx(bottom - top, zoom)}
      stroke={ACCENT}
      strokeWidth={1}
      dash={[4, 4]}
      opacity={0.6}
      strokeScaleEnabled={false}
      listening={false}
    />
  );
}
